import { Button } from "@racoon/ui";
import { useCurrentUser } from "~/shared/hooks/useCurrentUser";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { ConnectProto } from "@racoon/api";
import type { KeysOf } from "@racoon/i18n";
import { ViewSelector } from "~/shared/components/viewSelector";

export default function ProfilePage() {
  const currentUser = useCurrentUser();
  const t = useT();
  const { lang, setLang } = useLang();

  const languageOptions = [
    { value: "en", label: t(Namespace.Sidebar, "userMenu.languageEn") },
    { value: "kk", label: t(Namespace.Sidebar, "userMenu.languageKz") },
    { value: "ru", label: t(Namespace.Sidebar, "userMenu.languageRu") },
  ] as const;

  const displayName = currentUser
    ? `${currentUser.firstName} ${currentUser.lastName || ""}`.trim()
    : "User";

  const roleName = currentUser?.role
    ? t(
        Namespace.Enum,
        `USER_ROLE_${ConnectProto.UserRole[currentUser.role]}` as KeysOf<Namespace.Enum>,
      )
    : t(Namespace.Enum, "USER_ROLE_UNDEFINED");

  // Generate initials from first and last name
  const initials = [currentUser?.firstName?.[0], currentUser?.lastName?.[0]]
    .filter(Boolean)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <div className="flex flex-col h-full w-full bg-slate-50 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center px-2 py-4 shrink-0">
        <h1 className="text-xl font-bold tracking-tight text-slate-900">
          {t(Namespace.Sidebar, "profile")}
        </h1>
      </div>

      {/* Content */}
      <div className="flex-1 px-2 pb-4">
        <CardContentWrapper className="overflow-hidden" hasDivider={false}>
          {/* User Info Section */}
          <div className="bg-white shadow-sm flex items-end p-2">
            <div className="flex items-center gap-2">
              {/* Avatar */}
              <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-red-500 text-sm font-semibold text-white">
                {initials || "—"}
              </div>

              {/* User Details */}
              <div className="flex flex-col gap-1">
                <p className="font-semibold text-base leading-4 tracking-tight text-slate-900">
                  {displayName || "—"}
                </p>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-slate-600">
                  {roleName}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 p-4">
            <div>
              <ViewSelector
                options={languageOptions}
                activeSection={lang}
                setActiveSection={(value) => setLang(value)}
              />
            </div>
            {/* Logout Button Section */}
            <div className="flex items-center justify-center">
              <Button className="w-full !bg-brand-red" onClick={handleLogout}>
                {t(Namespace.Sidebar, "userMenu.logout")}
              </Button>
            </div>
          </div>
        </CardContentWrapper>
      </div>
    </div>
  );
}
